import React, { FunctionComponent, useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import InfoIcon from '@mui/icons-material/Info';
import LockIcon from '@mui/icons-material/Lock';
import RestoreIcon from '@mui/icons-material/Restore';

import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
} from '@mui/material';

import {
  Button,
  Card,
  NumberInput,
  Page,
  Select,
  TextInput,
  Typography
} from '../../components/v2/styled';
import { SnackSaved } from '../../components/v2/snacks/Saved';
import { Confirm } from '../../components/v2/dialogs/Confirm';

import { fetch_all, fetch_one } from "../../lib/v31lib";
import { loginAtom } from '../../lib/auth';

import HelpersLanguage from '../../actions/helpers/language';
import HelpersPractice from '../../actions/helpers/practice';

import { AdhProfile } from '../../models/AdhProfile';
import { AdhLink } from '../../models/AdhLink';
import { User } from '../../models/User';
import { FEATURES } from '../../features';

type Props = {}

const fetchAvailableProfiles = (login: any, i18n: any) => {
  return fetch_all<AdhProfile>(
    `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
    {
      object: 'adh_profile',
      fields: ['id', 'name', 'full_name', 'description', 'full_description', 'age_low', 'age_high', 'gender', 'is_pregnant', 'country'],
      sub_fields: {
        country: ['id', 'name']
      },
      order: "name ASC",
      per_page: 100,
      page: 0,
      filter: {
        search: '',
        advanced: {
          no_practice: '1',
          no_user: '1'
        }
      }
    },
    login
  );
}

const fetchProfile = (login: any, i18n: any, id: number) => {
  return fetch_one<AdhProfile>(
    `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
    {
      object: 'adh_profile',
      fields: ['id', 'name', 'full_name', 'adh_links', 'derived_id', 'derived'],
      sub_fields: {
        adh_links: ['id', 'nutritional_value', 'nutritional_value_id', 'lower_limit', 'higher_limit', 'maximum', 'is_tracked_client', 'measure_mode'],
        nutritional_value: ['id', 'name', 'is_grams_per_kilogram', 'measure', 'is_client_enabled', 'weight'],
        derived: ['id', 'name', 'full_name']
      },
      id: id
    },
    login
  );
}

const fetchMe = (login: any, i18n: any) => {
  return fetch_one<User>(
    `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
    {
      object: 'user',
      fields: ['id', 'birth_date', 'gender', 'country'],
      sub_fields: {
        country: ['id', 'name']
      },
      id: login?.id
    },
    login
  );
}

const describeAge = (profile: AdhProfile) => {
  if (!!profile.age_low && !!profile.age_high) return `${profile.age_low} - ${profile.age_high}`;
  if (!!profile.age_low) return `${profile.age_low}+`;
  if (!!profile.age_high) return `<= ${profile.age_high}`;
  return undefined;
}

const describeProfile = (profile: AdhProfile, t: any) => {
  let parts = [
    describeAge(profile),
    (profile.gender === 1 ? t("client.calculation.gender.male") : profile.gender === 0 ? t("client.calculation.gender.female") : undefined),
    (!!profile.is_pregnant ? t("client.calculation.gender.pregnant") : undefined),
    profile.country?.name
  ].filter(pp => !!pp);
  return parts.join(" / ");
}

const selectContentWithId = (field?: {[z:string]: string}, extraFallback?: string) => {
  let correct = (field || {})[HelpersLanguage.mapping_to_id(localStorage.getItem('i18nextLng') || 'nl')];
  return correct || (field || {})['1'] || (field || {})['nl'] || Object.values(field || {}).filter(fff => !!fff)[0] || extraFallback || '';
}

const limitUnit = (ll: AdhLink) => {
  if (ll.measure_mode === 1) return "g/kg";
  if (ll.measure_mode === 2) return "%EN";
  return ll.nutritional_value?.measure || '';
}

const formatNumber = (n?: number) => {
  return parseFloat((n || 0).toString()).toLocaleString('nl-BE', {maximumFractionDigits: 1});
}

const compareLinks = (a: AdhLink, b: AdhLink) => {
  const aw = a.nutritional_value?.weight ?? -1;
  const bw = b.nutritional_value?.weight ?? -1;
  if (aw !== bw) return bw - aw;
  return (a.nutritional_value?.name || '').localeCompare(b.nutritional_value?.name || '');
}

const fetchMyPractices = (login: any, i18n: any) => {
  return fetch_all<any>(
    `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
    {
      object: 'consent',
      fields: ['id', 'practice', 'practice_id', 'enabled', 'adh_profile_id'],
      sub_fields: {
        practice: ['id', 'modules']
      },
      filter: {
        search: '',
        advanced: {
          user_id: login?.id
        }
      }
    },
    login
  );
}

const formatBirthDate = (raw?: string) => {
  if (!raw) return undefined;
  const parts = raw.split('-');
  if (parts.length !== 3) return raw;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

export const ClientCalculation: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  const queryClient = useQueryClient();

  const [selectedProfileId, setSelectedProfileId] = useState<number | undefined>(login?.adh_profile_id);
  const [savedOpen, setSavedOpen] = useState<boolean>(false);
  const [draftLinks, setDraftLinks] = useState<AdhLink[] | undefined>(undefined);
  const [draftName, setDraftName] = useState<string | undefined>(undefined);
  const [searchLink, setSearchLink] = useState<string>('');
  const [editing, setEditing] = useState<boolean>(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState<boolean>(false);

  const {
    data: profiles,
    isLoading: profilesLoading,
    isError: profilesError,
    isSuccess: profilesSuccess,
  } = useQuery({
    queryKey: ["adh_profiles", "available"],
    queryFn: () => fetchAvailableProfiles(login, i18n),
    enabled: !!login?.id,
  });

  const {
    data: myPractices,
  } = useQuery({
    queryKey: ["my_practices_for_calculation", login?.id],
    queryFn: () => fetchMyPractices(login, i18n),
    enabled: !!login?.id,
  });

  const coachAdhId = ((myPractices || []).find((ccc: any) => !!ccc.enabled && !!ccc.adh_profile_id) || {} as any).adh_profile_id;
  const isCoachSet = !!coachAdhId;

  const detailProfileId = isCoachSet ? coachAdhId : (selectedProfileId || login?.adh_profile_id || login?.calculated_adh_profile_id);
  const {
    data: detailProfile,
    isLoading: detailProfileLoading,
  } = useQuery({
    queryKey: ["adh_profile", "detail", detailProfileId],
    queryFn: () => fetchProfile(login, i18n, detailProfileId as number),
    enabled: !!login?.id && !!detailProfileId,
  });

  const {
    data: me,
  } = useQuery({
    queryKey: ["user", "calculation_me", login?.id],
    queryFn: () => fetchMe(login, i18n),
    enabled: !!login?.id,
  });

  const canDoCalculated = FEATURES.CALCULATED_DIARY && !!HelpersPractice.practice_modules_v3({practice: {modules: (myPractices || []).filter(fff => !!fff.enabled && !!fff.practice).map(fff => fff.practice).flatMap(ppp => (ppp as any).modules || [])}, check: 'calculated_diary'});

  const mutationSetAdh = useMutation({
    mutationFn: (pars: {
      adh_profile_id?: number;
    }) => {
      let formData = new FormData();
      formData.append('object', 'user');
      formData.append('class_action', 'custom_api_set_adh_profile');
      formData.append('id', (login?.id || 0).toString());
      formData.append('adh_profile_id', (pars.adh_profile_id || 0).toString());
      return fetch_one<{custom_result: {success: boolean, adh_profile_id?: number}}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        formData,
        login
      );
    },
    onSuccess: (data, variables, context) => {
      setLogin({
        ...login,
        adh_profile_id: data.custom_result.adh_profile_id
      } as any);
      setSelectedProfileId(data.custom_result.adh_profile_id);
      setDraftLinks(undefined);
      setEditing(false);
      queryClient.invalidateQueries({queryKey: ["adh_profile"]});
      setSavedOpen(true);
    },
  });

  const mutationResetLinks = useMutation({
    mutationFn: () => {
      let formData = new FormData();
      formData.append('object', 'adh_profile');
      formData.append('class_action', 'custom_api_reset_client_links');
      formData.append('id', (detailProfile?.id || 0).toString());
      return fetch_one<{custom_result: {success: boolean}}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        formData,
        login
      );
    },
    onSuccess: (data, variables, context) => {
      setDraftLinks(undefined);
      setEditing(false);
      queryClient.invalidateQueries({queryKey: ["adh_profile"]});
      setSavedOpen(true);
    },
  });

  const mutationUpdateLinks = useMutation({
    mutationFn: (pars: {
      links: AdhLink[];
    }) => {
      let formData = new FormData();
      formData.append('object', 'adh_profile');
      formData.append('class_action', 'custom_api_update_client_links');
      formData.append('id', (detailProfile?.id || 0).toString());
      formData.append('name', draftName || '');
      pars.links.forEach((ll, i) => {
        formData.append(`links[${i}][nutritional_value_id]`, (ll.nutritional_value_id || 0).toString());
        formData.append(`links[${i}][is_tracked_client]`, (!!ll.is_tracked_client ? '1' : '0'));
        formData.append(`links[${i}][lower_limit]`, (ll.lower_limit ?? '').toString());
        formData.append(`links[${i}][higher_limit]`, (ll.higher_limit ?? '').toString());
        formData.append(`links[${i}][maximum]`, (ll.maximum ?? '').toString());
      });
      return fetch_one<{custom_result: {success: boolean}}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        formData,
        login
      );
    },
    onSuccess: (data, variables, context) => {
      setDraftLinks(undefined);
      queryClient.invalidateQueries({queryKey: ["adh_profile"]});
      setSavedOpen(true);
    },
  });

  useEffect(() => {
    if (!FEATURES.CALCULATED_DIARY || (myPractices !== undefined && !canDoCalculated)) navigate("/diary");
  }, [myPractices, canDoCalculated]);

  useEffect(() => {
    setDraftLinks(undefined);
    setEditing(false);
  }, [detailProfile?.id]);

  if (!FEATURES.CALCULATED_DIARY || myPractices === undefined || !canDoCalculated) {
    return null;
  }

  const activeDescription = isCoachSet
    ? t("client.calculation.coach_set")
    : !!login?.adh_profile_id && selectedProfileId === login?.adh_profile_id
    ? t("client.calculation.own_active")
    : !!selectedProfileId
      ? t("client.calculation.own_choice")
      : (!!login?.calculated_adh_profile_id
        ? t("client.calculation.calculated")
        : t("client.calculation.none"));

  const blueprintChanged = !!login?.adh_profile_id && selectedProfileId === login?.adh_profile_id;

  const isOwnProfile = !isCoachSet && !!detailProfile && !!login?.adh_profile_id && detailProfile.id === login?.adh_profile_id;

  useEffect(() => {
    if (!!isOwnProfile && !!detailProfile && draftLinks === undefined) {
      setDraftLinks([...(detailProfile.adh_links || [])].sort(compareLinks));
      setDraftName(selectContentWithId(detailProfile.full_name, detailProfile.name));
    }
  }, [detailProfile, isOwnProfile]);

  const sortedLinks = [...(detailProfile?.adh_links || [])].sort(compareLinks);
  const visibleLinks = ((!!isOwnProfile && !!draftLinks) ? draftLinks : sortedLinks)
    .filter(ll => {
      if (!searchLink) return !!(ll.lower_limit || ll.higher_limit || ll.maximum) || !!ll.is_tracked_client || !!isOwnProfile;
      return (ll.nutritional_value?.name || '').toLowerCase().indexOf(searchLink.toLowerCase()) > -1;
    });

  const linksDirty = !!draftLinks && (
    draftName !== selectContentWithId(detailProfile?.full_name, detailProfile?.name) ||
    JSON.stringify(draftLinks.map((ll: any) => [ll.nutritional_value_id, !!ll.is_tracked_client, ll.lower_limit, ll.higher_limit, ll.maximum])) !== JSON.stringify(sortedLinks.map((ll: any) => [ll.nutritional_value_id, !!ll.is_tracked_client, ll.lower_limit, ll.higher_limit, ll.maximum]))
  );

  return <Page title={t("client.calculation.title")}>
    {!!detailProfileLoading && !detailProfile ? (
      <Box sx={{textAlign: 'center', padding: 4}}>
        <Typography>{t("general.loading", "Laden...")}</Typography>
      </Box>
    ) : (
      <Grid container spacing={2}>
        <Card larger title={t("client.calculation.profile_choice.title")}>
          <Grid container spacing={2} sx={{paddingBottom: 2}}>
            {isCoachSet && (
              <Grid item xs={12}>
                <Alert severity="warning" icon={<LockIcon />}>
                  {t("client.calculation.coach_set_warning")}
                </Alert>
              </Grid>
            )}
            {!!login?.calculated_adh_profile_id && !selectedProfileId && !login?.adh_profile_id && !isCoachSet && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<InfoIcon />}>
                  {t("client.calculation.currently_calculated")}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Select
                id="adh_profile"
                caption={t("client.calculation.profile_choice.caption")}
                backend
                fullWidth
                displayEmpty
                disabled={isCoachSet}
                value={isCoachSet ? '' : (!!login?.adh_profile_id && selectedProfileId === login?.adh_profile_id ? '' : (selectedProfileId || ''))}
                setValue={(v) => {
                  setSelectedProfileId(!!v ? parseInt(v.toString(), 10) : undefined);
                }}
              >
                <MenuItem key="profile_none" value={''}>{activeDescription}</MenuItem>
                {(profiles || []).map(pp => (
                  <MenuItem key={`profile_${pp.id}`} value={pp.id}>
                    {selectContentWithId(pp.full_name, pp.name)}
                    {!!describeProfile(pp, t) && <span style={{opacity: 0.6, marginLeft: 6, fontSize: '0.8rem'}}>{describeProfile(pp, t)}</span>}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            {!isCoachSet && <Grid item xs={12}>
              <Alert severity="info" icon={<InfoIcon />} sx={{marginTop: 1}}>
                {t("client.calculation.blueprint_hint")}
              </Alert>
            </Grid>}
            <Grid item xs={12}>
              <Divider sx={{marginTop: 1}} />
              {!isCoachSet && <>
                {!!mutationSetAdh.isPending && <CircularProgress />}
                {!mutationSetAdh.isPending && <Button
                  id="save_adh_profile"
                  contained
                  label={t("client.general.buttons.submit", "Opslaan")}
                  disabled={mutationSetAdh.isPending || ((selectedProfileId || 0) !== 0 && !!login?.adh_profile_id && selectedProfileId === login?.adh_profile_id && !blueprintChanged)}
                  onClick={() => {
                    mutationSetAdh.mutate({
                      adh_profile_id: selectedProfileId
                    });
                  }}
                  sx={{
                    marginTop: 1
                  }}
                />}
              </>}
            </Grid>
          </Grid>
        </Card>

        <Card larger title={t("client.calculation.match_info.title")}>
          <Grid container spacing={2} sx={{paddingBottom: 2}}>
            <Grid item xs={12}>
              <Typography sx={{fontSize: '0.7rem'}}>{t("client.models.users.birth_date", "Geboortedatum")}</Typography>
              <Typography>{formatBirthDate(me?.birth_date) || '-'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{fontSize: '0.7rem'}}>{t("client.models.users.gender", "Geslacht")}</Typography>
              <Typography>
                {me?.gender === 1 ? t("general.genders.male") : me?.gender === 0 ? t("general.genders.female") : me?.gender === 2 ? t("general.genders.other") : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{fontSize: '0.7rem'}}>{t("client.models.users.country", "Land")}</Typography>
              <Typography>{me?.country?.name || '-'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" icon={<InfoIcon />}>
                {t("client.calculation.match_info.edit_hint")}
              </Alert>
            </Grid>
          </Grid>
        </Card>

        {!!isOwnProfile && !!draftLinks && !!editing && (
          <Grid item xs={12}>
            <Card
              title={t("client.calculation.profile_details.title")}
              huge
              actions={!!mutationUpdateLinks.isPending || !!mutationResetLinks.isPending ? <CircularProgress /> : (<Box sx={{width: '100%'}}>
                <Divider sx={{marginBottom: 1}} />
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                  <Button
                    id="back_adh_links"
                    label={t("client.calculation.profile_details.back")}
                    onClick={() => {
                      setDraftLinks(undefined);
                      setEditing(false);
                    }}
                  />
                  <Box sx={{flexGrow: 1}} />
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Button
                      id="reset_adh_links"
                      label={t("client.calculation.profile_details.reset")}
                      disabled={!detailProfile?.derived_id}
                      startIcon={<RestoreIcon />}
                      onClick={() => {
                        setResetConfirmOpen(true);
                      }}
                    />
                    <Button
                      id="save_adh_links"
                      contained
                      label={t("client.general.buttons.submit", "Opslaan")}
                      disabled={!linksDirty}
                      onClick={() => {
                        mutationUpdateLinks.mutate({links: draftLinks});
                      }}
                    />
                  </Box>
                </Box>
              </Box>)}
            >
              <Grid container spacing={2} sx={{paddingBottom: 1}}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextInput
                    id="own_profile_name"
                    caption={t("client.calculation.profile_details.name")}
                    value={draftName || ''}
                    onChange={(v: string) => {setDraftName(v);}}
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.06)',
                      border: '1px solid rgba(0, 0, 0, 0.2)',
                      borderRadius: 1
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={8} sx={{alignSelf: 'end'}}>
                  <TextInput
                    id="search_link"
                    value={searchLink}
                    onChange={setSearchLink}
                    caption={t("client.calculation.profile_details.search")}
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.06)',
                      border: '1px solid rgba(0, 0, 0, 0.2)',
                      borderRadius: 1
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  {!!detailProfile?.derived && <Typography sx={{fontSize: '0.85rem', paddingBottom: 0.5}}>
                    {t("client.calculation.profile_details.based_on")}: {selectContentWithId(detailProfile.derived.full_name, detailProfile.derived.name)}
                  </Typography>}
                  <Typography sx={{fontStyle: 'italic', fontSize: '0.8rem'}}>
                    {t("client.calculation.profile_details.own_hint")}
                  </Typography>
                </Grid>
                {visibleLinks.filter(fff => !!fff.nutritional_value?.is_client_enabled).map((ll) => {
                  const updateDraftLink = (changes: Partial<AdhLink>) => {
                    let newLinks = [...(draftLinks || [])];
                    const idx = newLinks.findIndex(nn => nn.nutritional_value_id === ll.nutritional_value_id);
                    if (idx > -1) newLinks[idx] = {...newLinks[idx], ...changes};
                    setDraftLinks(newLinks);
                  };
                  const toggleTracked = () => {
                    updateDraftLink({is_tracked_client: !ll.is_tracked_client});
                  };
                  return (
                  <Grid item xs={12} sm={6} md={4} key={`link_${ll.nutritional_value_id}`}>
                    <Box
                      // onClick={(e) => {
                      //   if (!['INPUT', 'BUTTON'].includes((e.target as HTMLElement).tagName)) toggleTracked();
                      // }}
                      sx={{
                      backgroundColor: 'white',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      borderRadius: 2,
                      padding: 1.5,
                      // cursor: 'pointer',
                      opacity: !!ll.is_tracked_client ? 1 : 0.55
                    }}>
                      <Box sx={{display: 'flex', alignItems: 'center'}}>
                        <Checkbox
                          checked={!!ll.is_tracked_client}
                          onChange={toggleTracked}
                        />
                        <Typography sx={{fontWeight: 'bold'}}>{ll.nutritional_value?.name}</Typography>
                      </Box>
                      <Box sx={{display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap'}}>
                        <Box sx={{flex: 1, minWidth: '80px'}}>
                          <NumberInput
                            id={`limit_lower_${ll.nutritional_value_id}`}
                            caption={t("client.calculation.limits.lower")}
                            value={ll.lower_limit}
                            onChange={(n: number | undefined) => {
                              updateDraftLink({lower_limit: n});
                            }}
                            endIcon={<InputAdornment position="end">{limitUnit(ll)}</InputAdornment>}
                            sx={{
                              backgroundColor: 'rgba(0, 0, 0, 0.06)',
                              border: '1px solid rgba(0, 0, 0, 0.2)',
                              borderRadius: 1
                            }}
                          />
                        </Box>
                        <Box sx={{flex: 1, minWidth: '80px'}}>
                          <NumberInput
                            id={`limit_higher_${ll.nutritional_value_id}`}
                            caption={t("client.calculation.limits.recommended")}
                            value={ll.higher_limit}
                            onChange={(n: number | undefined) => {
                              updateDraftLink({higher_limit: n});
                            }}
                            endIcon={<InputAdornment position="end">{limitUnit(ll)}</InputAdornment>}
                            sx={{
                              backgroundColor: 'rgba(0, 0, 0, 0.06)',
                              border: '1px solid rgba(0, 0, 0, 0.2)',
                              borderRadius: 1
                            }}
                          />
                        </Box>
                        <Box sx={{flex: 1, minWidth: '80px'}}>
                          <NumberInput
                            id={`limit_maximum_${ll.nutritional_value_id}`}
                            caption={t("client.calculation.limits.maximum")}
                            value={ll.maximum}
                            onChange={(n: number | undefined) => {
                              updateDraftLink({maximum: n});
                            }}
                            endIcon={<InputAdornment position="end">{limitUnit(ll)}</InputAdornment>}
                            sx={{
                              backgroundColor: 'rgba(0, 0, 0, 0.06)',
                              border: '1px solid rgba(0, 0, 0, 0.2)',
                              borderRadius: 1
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  );
                })}
              </Grid>
            </Card>
          </Grid>
        )}

        {!(!!isOwnProfile && !!draftLinks && !!editing) && (
          <Grid item xs={12}>
            <Card
              title={t("client.calculation.profile_details.title")}
              actions={!!isOwnProfile && !editing ? (
                <Button
                  id="edit_adh_links"
                  label={t("client.calculation.profile_details.edit")}
                  onClick={() => {
                    setDraftLinks([...(detailProfile?.adh_links || [])].sort(compareLinks));
                    setDraftName(selectContentWithId(detailProfile?.full_name, detailProfile?.name));
                    setEditing(true);
                  }}
                />
              ) : undefined}
            >
              {!detailProfile && (
              <Typography sx={{fontStyle: 'italic'}}>{t("client.calculation.profile_details.empty")}</Typography>
            )}
            {!!detailProfile && (
              <Box sx={{paddingBottom: 2}}>
                <Typography variant="h6">
                  {selectContentWithId(detailProfile.full_name, detailProfile.name)}
                </Typography>
                {!!detailProfile.derived && <Typography sx={{opacity: 0.7, fontSize: '0.85rem'}}>
                  {t("client.calculation.profile_details.based_on")}: {selectContentWithId(detailProfile.derived.full_name, detailProfile.derived.name)}
                </Typography>}
                <Divider sx={{my: 1}} />
                {(detailProfile.adh_links || []).filter(fff => !!fff.nutritional_value?.is_client_enabled)
                  .filter(ll => !!(ll.lower_limit || ll.higher_limit || ll.maximum) || !!ll.is_tracked_client)
                  .sort((a, b) => {
                    let tracked = (!!b.is_tracked_client ? 1 : 0) - (!!a.is_tracked_client ? 1 : 0);
                    if (tracked !== 0) return tracked;
                    return compareLinks(a, b);
                  })
                  .map(ll => (
                    <Box key={`link_${ll.nutritional_value_id}`} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, flexWrap: 'wrap', gap: 1}}>
                      <Typography sx={{fontWeight: 'bold'}}>{ll.nutritional_value?.name}</Typography>
                      <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
                        {!!ll.lower_limit && !!ll.higher_limit && <Chip
                          size="small"
                          color="primary"
                          label={`${formatNumber(ll.lower_limit)} - ${formatNumber(ll.higher_limit)} ${limitUnit(ll)}`.trim()}
                        />}
                        {!!ll.lower_limit && !ll.higher_limit && <Chip
                          size="small"
                          label={`${t("client.calculation.limits.min")} ${formatNumber(ll.lower_limit)} ${limitUnit(ll)}`.trim()}
                        />}
                        {!ll.lower_limit && !!ll.higher_limit && <Chip
                          size="small"
                          label={`<= ${formatNumber(ll.higher_limit)} ${limitUnit(ll)}`.trim()}
                        />}
                        {!!ll.maximum && <Chip
                          size="small"
                          color="error"
                          label={`${t("client.calculation.limits.max")} ${formatNumber(ll.maximum)} ${limitUnit(ll)}`.trim()}
                        />}
                      </Box>
                    </Box>
                  ))}
              </Box>
            )}
          </Card>
        </Grid>
        )}
      </Grid>
    )}

    <Confirm
      open={resetConfirmOpen}
      setOpen={setResetConfirmOpen}
      title={t("client.calculation.profile_details.reset_confirm.title")}
      content={t("client.calculation.profile_details.reset_confirm.content")}
      execute={() => {
        setResetConfirmOpen(false);
        mutationResetLinks.mutate();
      }}
    />

    <SnackSaved open={savedOpen} setOpen={setSavedOpen} />
  </Page>;
}
